import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import AdminDialog, {AdminDialogProperties, AdminDialogState} from "./AdminDialog";
import AdminDialogType from "../AdminDialogType";
import Administration from "../../session/Administration";
import {
	ConfigurationType,
	Server,
	ServerConfigUserStorage,
	UserServerCheck
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {InputText} from "primereact/inputtext";
import {UserSourceDialogState} from "./UserSourceDialog";
import ValidateUserSourceDialogFields from "../field/ValidateUserSourceDialogFields";
import Translate from "../../../shared/translation/Translate";
import Helper from "../../../shared/components/Helper";
import Icon from "../../../shared/components/Icon";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import Info, {InfoType} from "../../../shared/components/Info";

interface ValidateUserSourceDialogProperties extends AdminDialogProperties<UserServerCheck> {
	onError: (exception: Error) => Promise<boolean>,
	setParentState: (state: Pick<UserSourceDialogState, keyof UserSourceDialogState>, callback?: () => void) => void,
	userConfiguration: ServerConfigUserStorage
}

export class ValidateUserSourceDialogDefinition extends AdminDialog<UserServerCheck,
	ValidateUserSourceDialogProperties, AdminDialogState<UserServerCheck>> {
	public renderContents(): ReactNode {
		let parameter: UserServerCheck | undefined = this.state.parameter;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		return (
			<fieldset className="p-col-12 p-grid">
				<div className="p-col-12 p-grid">
					<label className="p-col-12 p-sm-4" htmlFor="control-ldap-user">
						<Translate {...this.props} data="validate_user_source.user" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={UserServerCheck.getNameDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8 p-grid">
						<InputText id="control-ldap-user"
								   value={FunctionHelper.applyValue(parameter?.name, UserServerCheck.getNameDefault())}
								   onInput={(e) => this.onChange(
									   ValidateUserSourceDialogFields.USER, e.currentTarget.value
								   )}/>
					</div>
				</div>
			</fieldset>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["validate-user-source-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					resizable={false} appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.admin.user_source.test"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-user source"}/>
					<Translate {...this.props} data="validate_user_source.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected onChange(field: ValidateUserSourceDialogFields, value: any): void {
		let parameter: UserServerCheck | undefined = this.state.parameter as UserServerCheck;
		let errors: Map<any, string> = this.state.fieldErrors;

		switch (field) {
			case ValidateUserSourceDialogFields.USER:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.name = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		}, () => {
			super.onChange(field, value);
		});
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let validated: boolean = false;
		let administration: Administration = this.props.context.session.getAdministration();
		let UserServerCheck: UserServerCheck = this.state.parameter as UserServerCheck;
		let serverConfiguration: Server =
			(await administration.getConfiguration(ConfigurationType.Server) as Server).clone();
		serverConfiguration.user = this.props.userConfiguration!;

		try {
			await administration.validateConfiguration(ConfigurationType.Server, serverConfiguration, [
				UserServerCheck
			]);

			validated = true;
		} catch (exception: any) {
			if (await this.props.onError(exception)) {
				await this.submit();
				return;
			}
		}

		this.props.setParentState({
			validated: validated
		} as UserSourceDialogState);
		this.hide();
	}

	protected renderFooter(): ReactNode {
		return (
			<div className="p-col-12 p-grid">
				<div className="p-col-12 p-grid">
					<Button label={Translate.value("buttons.test")} disabled={!this.isValid()}
							icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-check")}
							className="primary-button" onClick={() => this.submit()}/>
					{this.state.submitted ?
						<Button label={Translate.value("buttons.close")} icon="icon icon-close"
								onClick={() => this.hide()} className="dialog-button"/> :
						<Button label={Translate.value("buttons.cancel")} icon="icon icon-close"
								onClick={() => this.hide()} className="dialog-button"/>
					}
				</div>
			</div>
		);
	}

	protected isValid(): boolean {
		if (!this.mounted || !this.props.visible) {
			return false;
		}

		return !this.state.submitted;
	}

	protected getDialogType(): AdminDialogType {
		return AdminDialogType.VALIDATE_USER_SOURCE;
	}
}

const ValidateUserSourceDialog = React.memo(ValidateUserSourceDialogDefinition,
	(
		prevProps: Readonly<AdminDialogProperties<UserServerCheck>>,
		nextProps: Readonly<AdminDialogProperties<UserServerCheck>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default ValidateUserSourceDialog;